import { Link } from 'react-router-dom';
import { ArrowRight, Check, HelpCircle, Phone, Zap, Shield, Clock, DollarSign, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEOHead from '../components/SEOHead';
import { getClusterLinks } from '../data/internalLinks';
import { PORTAL_LINKS } from '../constants/site';

const ICON_MAP = {
  check: Check, zap: Zap, shield: Shield, clock: Clock, dollar: DollarSign, phone: Phone
};

const COMMERCIAL_PRIMARY_PORTAL_SLUGS = new Set([
  'commercial-vehicle-finance',
  'low-doc-asset-finance',
]);

export default function SEOLandingPage({ content }) {
  const {
    slug, title, metaTitle, metaDescription, keywords, category,
    heroHeadline, heroSubheadline, heroDescription,
    introSections = [],
    howItWorks = [],
    financeOptions = [],
    eligibility = [],
    exampleScenario,
    features = [],
    faqs = [],
    relatedPages = [],
    hubLinks,
    schema = [],
    ctaText = 'Check Your Options'
  } = content;

  const priorityServicePath = slug.includes('car')
    ? '/car-loans'
    : slug.includes('personal')
      ? '/personal-loans'
      : '/business-finance';

  const useCommercialPortalPrimaryCta = COMMERCIAL_PRIMARY_PORTAL_SLUGS.has(slug);
  const primaryCtaHref = useCommercialPortalPrimaryCta ? PORTAL_LINKS.COMMERCIAL : priorityServicePath;

  const primaryCtaLabel = ctaText === 'See What You Can Get Approved For' ? 'See What You Can Get Approved For' : 'Check Your Options';

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    ...(category === 'asset' ? [{ name: 'Services', path: '/services' }] : []),
    ...(category === 'product' ? [{ name: 'Services', path: '/services' }] : []),
    { name: title, path: `/${slug}` }
  ];

  return (
    <>
      <SEOHead
        title={metaTitle || `${title} Australia`}
        description={metaDescription}
        canonical={`/${slug}`}
        keywords={keywords}
        breadcrumbs={breadcrumbs}
        faqs={faqs}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-navy-900"></div>
        <div className="relative container mx-auto px-4 py-20 lg:py-28">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            {breadcrumbs.map((bc, i) => (
              <span key={bc.path} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={bc.path} className="hover:text-purple-400">{bc.name}</Link>
                ) : (
                  <span className="text-purple-400">{bc.name}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white uppercase leading-tight mb-6">
              {heroHeadline}
            </h1>
            {heroSubheadline && (
              <p className="text-lg text-purple-400 font-semibold mb-4">{heroSubheadline}</p>
            )}
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl">
              {heroDescription}
            </p>
            <p className="text-sm text-gray-400 mb-5" data-testid={`seo-route-priority-${slug}`}>
              Start with the best matching product path, then continue via the secure customer portal.
            </p>
            <div className="flex flex-wrap gap-4">
              {useCommercialPortalPrimaryCta ? (
                <a href={primaryCtaHref} data-testid={`seo-hero-primary-link-${slug}`}>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg rounded-xl" data-testid={`seo-hero-primary-cta-${slug}`}>
                    {primaryCtaLabel} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              ) : (
                <Link to={primaryCtaHref} data-testid={`seo-hero-primary-link-${slug}`}>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg rounded-xl" data-testid={`seo-hero-primary-cta-${slug}`}>
                    {primaryCtaLabel} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              )}
              <a href={PORTAL_LINKS.COMMERCIAL} data-testid={`seo-hero-secondary-link-${slug}`}>
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:text-white px-8 py-6 text-lg rounded-xl" data-testid={`seo-hero-secondary-cta-${slug}`}>
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Content Sections */}
      {introSections.map((section, idx) => (
        <section key={idx} className={`py-16 ${idx % 2 === 0 ? 'bg-navy-800' : 'bg-navy-900'}`}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-6">{section.heading}</h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="text-gray-300 leading-relaxed mb-4">{p}</p>
              ))}
              {section.bullets && (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                  {section.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-300">
                      <Check className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* Local Internal Link Hub */}
      {hubLinks?.categories?.length > 0 && (
        <section className="py-16 bg-navy-900" data-testid="seo-state-hub-links">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-3">
              {hubLinks.heading || 'State Finance Resource Hub'}
            </h2>
            <p className="text-gray-400 mb-8 text-sm">Explore calculators, product pages, and guides to complete your finance planning.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {hubLinks.categories.map((group, idx) => (
                <div key={idx} className="bg-navy-800 border border-white/5 rounded-xl p-5">
                  <h3 className="text-purple-300 font-semibold uppercase text-xs tracking-wide mb-3">{group.title}</h3>
                  <ul className="space-y-2">
                    {group.links.map((link, i) => (
                      <li key={i}>
                        <Link to={link.path} className="text-gray-300 hover:text-purple-300 text-sm inline-flex items-center gap-1">
                          {link.title} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      {howItWorks.length > 0 && (
        <section className="py-16 bg-navy-800">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase text-center mb-12">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {howItWorks.map((step, idx) => (
                <div key={idx} className="bg-navy-900 border border-white/5 rounded-xl p-6 text-center relative">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                    {idx + 1}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Finance Options */}
      {financeOptions.length > 0 && (
        <section className="py-16 bg-navy-900">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase text-center mb-12">
              Finance Options Available
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {financeOptions.map((opt, idx) => (
                <div key={idx} className="bg-navy-800 border border-white/5 rounded-xl p-6 hover:border-purple-500/30 transition-colors">
                  <h3 className="text-white font-semibold text-lg mb-3">{opt.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{opt.description}</p>
                  {opt.link && (
                    <Link to={opt.link} className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                      Learn more <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features / Key Benefits */}
      {features.length > 0 && (
        <section className="py-16 bg-navy-800">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase text-center mb-12">
              Key Benefits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {features.map((f, idx) => (
                <div key={idx} className="bg-navy-900 rounded-xl p-6 border border-white/5">
                  <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center text-purple-400 mb-4">
                    {(() => { const Icon = ICON_MAP[f.icon] || Check; return <Icon className="h-5 w-5" />; })()}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{f.title}</h3>
                  <p className="text-gray-400 text-sm">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Eligibility */}
      {eligibility.length > 0 && (
        <section className="py-16 bg-navy-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-8">
              Eligibility Requirements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {eligibility.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-navy-800 rounded-lg border border-white/5">
                  <Check className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Example Scenario */}
      {exampleScenario && (
        <section className="py-16 bg-navy-800">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-8">
              Example Finance Scenario
            </h2>
            <div className="bg-navy-900 rounded-xl border border-purple-500/20 p-8">
              <p className="text-gray-300 mb-6">{exampleScenario.description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {Object.entries(exampleScenario.details).map(([key, val]) => (
                  <div key={key}>
                    <p className="text-gray-500 text-sm">{key}</p>
                    <p className="text-white font-bold text-xl">{val}</p>
                  </div>
                ))}
              </div>
              {exampleScenario.note && (
                <p className="text-gray-500 text-xs mt-6 italic">{exampleScenario.note}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Mid-page CTA */}
      <section className="py-12 bg-navy-900">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-heading text-white uppercase mb-2">Find Your Best Finance Option</h3>
              <p className="text-gray-300 text-sm">Compare rates from 40+ lenders quickly. No obligation, no credit check.</p>
            </div>
            <Link to={priorityServicePath} className="flex-shrink-0" data-testid={`seo-mid-primary-link-${slug}`}>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-5 text-base rounded-xl whitespace-nowrap" data-testid={`seo-mid-primary-cta-${slug}`}>
                Check Your Options <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-16 bg-navy-800">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-8">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="bg-navy-800 rounded-xl border border-white/5 px-6">
                  <AccordionTrigger className="text-white text-left py-5 hover:text-purple-400">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-300 pb-5 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* Clustered Related Finance Guides */}
      {(() => {
        const clusters = getClusterLinks(slug, category);
        return (
          <section className="py-16 bg-navy-900" data-testid="related-finance-guides">
            <div className="container mx-auto px-4 max-w-5xl">
              <h2 className="text-2xl sm:text-3xl font-heading text-white uppercase mb-10">
                Related Finance Guides
              </h2>

              {/* Asset Finance Cluster */}
              {clusters.assetFinance.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Asset Finance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clusters.assetFinance.map((page, idx) => (
                      <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-colors group">
                        <h4 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-2">{page.title}</h4>
                        <p className="text-gray-400 text-sm mb-3">{page.desc}</p>
                        <span className="text-purple-400 text-sm flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Finance Structures Cluster */}
              {clusters.financeStructures.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Finance Structures</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clusters.financeStructures.map((page, idx) => (
                      <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-colors group">
                        <h4 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-2">{page.title}</h4>
                        <p className="text-gray-400 text-sm mb-3">{page.desc}</p>
                        <span className="text-purple-400 text-sm flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Finance Tools Cluster */}
              {clusters.financeTools.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Finance Tools</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clusters.financeTools.map((page, idx) => (
                      <Link key={idx} to={page.path} className="bg-navy-800 rounded-xl p-5 border border-white/5 hover:border-purple-500/30 transition-colors group flex items-start gap-3">
                        <Calculator className="h-5 w-5 text-purple-400 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-white font-semibold group-hover:text-purple-400 transition-colors mb-1">{page.title}</h4>
                          <p className="text-gray-400 text-sm">{page.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* State Guides Cluster */}
              {clusters.stateGuides?.length > 0 && (
                <div className="mb-10" data-testid="related-state-guides-cluster">
                  <h3 className="text-lg font-heading text-purple-400 uppercase tracking-wider mb-4">Finance Across Australia</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {clusters.stateGuides.map((page, idx) => (
                      <Link
                        key={idx}
                        to={page.path}
                        className="bg-navy-800 rounded-xl p-4 border border-white/5 hover:border-purple-500/30 transition-colors group"
                        data-testid={`related-state-guide-link-${page.path.replace(/[^a-z0-9]+/gi, '-')}`}
                      >
                        <h4 className="text-white text-sm font-semibold group-hover:text-purple-400 transition-colors mb-1">{page.title}</h4>
                        <p className="text-gray-400 text-xs mb-2">{page.desc}</p>
                        <span className="text-purple-400 text-xs flex items-center gap-1">Open guide <ArrowRight className="h-3.5 w-3.5" /></span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-b from-navy-900 to-purple-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading text-white uppercase mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Choose your finance path first, then complete the guided application when you're ready.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={priorityServicePath} data-testid={`seo-bottom-primary-link-${slug}`}>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-lg rounded-xl" data-testid={`seo-bottom-primary-cta-${slug}`}>
                Check Your Options <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid={`seo-bottom-secondary-link-${slug}`}>
              <Button variant="outline" className="border-gray-600 text-gray-300 hover:text-white px-10 py-6 text-lg rounded-xl" data-testid={`seo-bottom-secondary-cta-${slug}`}>
                See What You Can Get Approved For
              </Button>
            </a>
          </div>
          <p className="text-gray-500 text-sm mt-6">
            No obligation. No credit check. Get your options quickly.
          </p>
        </div>
      </section>
    </>
  );
}
